'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import Container from '@/components/ui/Container';
import { IHireProcess } from '@/components/type/hireUs/hireAiType';

interface HireAiDevelopersProps {
  data: IHireProcess;
}

// Tabs window config
const TAB_MIN = 110; // min width of one tab (px)
const TAB_GAP = 6; // gap between tabs (px)

export default function HireAiDevelopers({ data }: HireAiDevelopersProps) {
  const steps = data?.steps ?? [];
  const [active, setActive] = useState(0);
  const [cardWidth, setCardWidth] = useState(0);
  const [viewportWidth, setViewportWidth] = useState(0);
  const [tabsWidth, setTabsWidth] = useState(0);
  // gap between cards (gap-5 / md:gap-6 / lg:gap-8) is read from the DOM
  const [gap, setGap] = useState(20);
  // 1024px–1279px: show exactly 3 cards, 4th card must not peek out
  const [exactFit, setExactFit] = useState(false);
  // 1536px+ (e.g. 1920): only 5 tabs are visible at a time, rest slide in
  const [maxTabs, setMaxTabs] = useState(Infinity);

  const firstCardRef = useRef<HTMLDivElement | null>(null);
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const tabsRef = useRef<HTMLDivElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);

  const measure = useCallback(() => {
    if (firstCardRef.current) {
      setCardWidth(firstCardRef.current.offsetWidth);
    }
    if (viewportRef.current) {
      setViewportWidth(viewportRef.current.offsetWidth);
    }
    if (tabsRef.current) {
      setTabsWidth(tabsRef.current.offsetWidth);
    }
    setExactFit(
      window.matchMedia('(min-width: 1024px) and (max-width: 1279px)').matches,
    );
    setMaxTabs(window.matchMedia('(min-width: 1536px)').matches ? 5 : Infinity);
    if (trackRef.current) {
      const g = parseFloat(getComputedStyle(trackRef.current).columnGap);
      if (!Number.isNaN(g)) setGap(g);
    }
  }, []);

  useEffect(() => {
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure]);

  // How many full cards fit in the visible area (3 desktop, 2 tablet, 1 mobile)
  // small epsilon so rounding of offsetWidth never drops a card
  const visibleCount =
    cardWidth > 0
      ? Math.max(
          1,
          Math.floor((viewportWidth + gap) / (cardWidth + gap) + 0.01),
        )
      : 3;

  // The track stops when the last card reaches the last visible position
  const maxOffset = Math.max(steps.length - visibleCount, 0);
  const offset = Math.min(active, maxOffset);

  // Arrows move the active step through ALL steps (tabs highlight + white card),
  // while the cards track itself stops at maxOffset (offset = min(active, maxOffset)).
  const goPrev = () => setActive(Math.max(0, active - 1));
  const goNext = () => setActive(Math.min(steps.length - 1, active + 1));

  const prevDisabled = active === 0;
  const nextDisabled = active >= steps.length - 1;

  if (!steps.length) return null;

  // Tabs window: how many tabs fit (each tab min TAB_MIN px)
  const tabsVisible =
    tabsWidth > 0
      ? Math.min(
          steps.length,
          maxTabs,
          Math.max(1, Math.floor((tabsWidth + TAB_GAP) / (TAB_MIN + TAB_GAP))),
        )
      : Math.min(steps.length, 5);

  // Keep the active tab in the middle of the window where possible
  const tabStart = Math.min(
    Math.max(0, active - Math.floor(tabsVisible / 2)),
    Math.max(steps.length - tabsVisible, 0),
  );

  const tabWidthCalc = `((100% - ${TAB_GAP * (tabsVisible - 1)}px) / ${tabsVisible})`;

  return (
    <section className="w-full overflow-hidden bg-black font-opt text-white">
      {/* section overflow-hidden: cards bleed to the right only up to the screen edge */}
      <Container>
        {/* Heading */}
        <div className="mx-auto text-center">
          <h2 className="font-opt text-3xl font-semibold leading-tight md:text-4xl lg:text-[42px]">
            {data.headingText}
          </h2>
          <p className="mx-auto mt-3 max-w-[927px] font-opt text-base font-normal leading-6 text-white/80 md:mt-4 md:text-lg md:leading-7 lg:text-xl">
            {data.descriptionText}
          </p>
        </div>

        {/* Tabs + arrows */}
        <div className="mx-auto mt-10 flex w-full max-w-[1095px] items-center gap-3 md:mt-12 md:gap-5 lg:mt-16 2xl:max-w-[1460px]">
          <div className="min-w-0 flex-1 rounded-xl border border-white/10 bg-[#0d0d0d] p-1.5">
            <div ref={tabsRef} className="overflow-hidden">
              <div
                className="relative flex gap-[6px] transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]"
                style={{
                  transform: `translateX(calc(${-tabStart} * (${tabWidthCalc} + ${TAB_GAP}px)))`,
                }}
              >
                {/* Sliding active highlight */}
                <div
                  className="pointer-events-none absolute bottom-0 left-0 top-0 z-10 rounded-[10px] bg-gradient-to-r from-[#1d5ea8] to-[#00a8e8] ring-1 ring-white/25 transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]"
                  style={{
                    width: `calc${tabWidthCalc}`,
                    transform: `translateX(calc(${active} * (100% + ${TAB_GAP}px)))`,
                  }}
                />

                {/* Tab buttons */}
                {steps.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setActive(i)}
                    style={{ width: `calc${tabWidthCalc}` }}
                    className={`relative z-20 shrink-0 rounded-[10px] border px-3 py-2 font-opt text-xs transition-colors duration-500 md:px-4 md:text-sm ${
                      i === active
                        ? 'border-transparent bg-transparent font-medium text-white'
                        : 'text-gray-300 border-white/10 bg-[#161616] hover:text-white'
                    }`}
                  >
                    {`Step ${i + 1}`}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="flex shrink-0 gap-2">
            <button
              type="button"
              onClick={goPrev}
              disabled={prevDisabled}
              aria-label="Previous step"
              className={`flex h-10 w-10 items-center justify-center rounded-full transition-all duration-300 md:h-11 md:w-11 ${
                prevDisabled
                  ? 'text-gray-300 cursor-not-allowed bg-[#454545]'
                  : 'bg-white text-black hover:scale-105'
              }`}
            >
              <ArrowIcon direction="left" />
            </button>
            <button
              type="button"
              onClick={goNext}
              disabled={nextDisabled}
              aria-label="Next step"
              className={`flex h-10 w-10 items-center justify-center rounded-full transition-all duration-300 md:h-11 md:w-11 ${
                nextDisabled
                  ? 'text-gray-300 cursor-not-allowed bg-[#454545]'
                  : 'bg-white text-black hover:scale-105'
              }`}
            >
              <ArrowIcon direction="right" />
            </button>
          </div>
        </div>

        {/* Slider: clipped on the left only, cards bleed off to the right */}
        <div
          ref={viewportRef}
          className="mt-10 md:mt-12"
          style={{
            // Single card (mobile) or exactly 3 cards (1024–1279px): clip both sides so the next card never peeks.
            // Multiple cards: bleed off to the right.
            clipPath:
              visibleCount === 1 || exactFit
                ? 'inset(-40px 0 -40px 0)'
                : 'inset(-40px -100vw -40px 0)',
          }}
        >
          <div
            ref={trackRef}
            className="flex gap-5 transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] md:gap-6 lg:gap-8 2xl:gap-6"
            style={{
              transform: `translateX(-${offset * (cardWidth + gap)}px)`,
            }}
          >
            {steps.map((s, i) => {
              const isActive = i === active;
              return (
                <div
                  key={i}
                  ref={i === 0 ? firstCardRef : undefined}
                  onClick={() => setActive(i)}
                  className={`relative w-full shrink-0 cursor-pointer rounded-xl p-5 font-opt transition-colors duration-500 md:w-[calc((100%-24px)/2)] md:p-6 lg:w-[calc((100%-64px)/3)] lg:p-5 xl:p-6 2xl:w-[30%] ${
                    isActive ? 'bg-white text-black' : 'bg-[#1a1a1a] text-white'
                  }`}
                >
                  <span className="absolute -top-3 left-5 rounded-sm bg-gradient-to-r from-[#1d5ea8] to-[#00a8e8] px-3 py-0.5 font-opt text-xs font-medium text-white md:left-8 lg:left-5 xl:left-8">
                    {`Step ${i + 1}`}
                  </span>

                  <h3 className="mt-2 min-h-[48px] font-opt text-lg font-medium leading-snug md:min-h-[56px] md:text-xl lg:min-h-[52px] lg:text-lg xl:min-h-[56px] xl:text-[22px]">
                    {s.title}
                  </h3>

                  <div
                    className={`mt-4 space-y-3 font-opt text-sm leading-6 transition-colors duration-500 md:mt-5 lg:text-[13px] lg:leading-5 xl:text-base xl:leading-6 ${
                      isActive ? 'text-gray-800' : 'text-gray-300'
                    }`}
                  >
                    <p>
                      <span className="font-semibold">You receive:</span>{' '}
                      {s.receive}
                    </p>
                    <p>
                      <span className="font-semibold">Typical time:</span>{' '}
                      {s.time}
                    </p>
                    <p>
                      <span className="font-semibold">Moves on when:</span>{' '}
                      {s.movesOn}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Container>
    </section>
  );
}

function ArrowIcon({ direction }: { direction: 'left' | 'right' }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={direction === 'left' ? 'rotate-180' : ''}
    >
      <path d="M5 12h14" />
      <path d="m13 6 6 6-6 6" />
    </svg>
  );
}
